import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import BasePhoneInput, { PickerData, ReactNativePhoneInputProps } from "react-native-phone-input"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import _ from "lodash"
import { Appearance, Dimensions, Platform, StyleProp, TextInput, TextStyle, ViewStyle } from "react-native"
import { Icon, useDisclose, View } from "native-base"
import { Image } from "../../image/image"
import { ImageStyle } from "react-native-fast-image"
import { Input, InputProps } from "../input"
import { PickerActionsheet } from "../../picker-actionsheet/picker-actionsheet"
import { Text } from "../../text/text"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { ListRenderItem } from "../../flat-list/flat-list"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export interface PhoneInputProps<TextComponentType extends React.ComponentType = typeof TextInput> extends ReactNativePhoneInputProps<TextComponentType> {
  textProps?: React.ComponentProps<TextComponentType> & InputProps;
  colorScheme?: "light" | "dark";

  isInvalid?: boolean;
}

const CONTAINER: ViewStyle = {
  borderWidth: 1,
}

const FLAG: ViewStyle = {
  flexDirection: "row",
  flexGrow: 1,
  justifyContent: "center",
  alignItems: "center"
}

const FLAG_IMAGE: StyleProp<ImageStyle> = {
  width: 21,
  height: 15.75,
  borderWidth: 0.5,
  borderColor: color.palette.lineDark,
  borderRadius: 2,
  backgroundColor: color.dim
}

const TEXT: TextStyle = {
  height: "40px",
  padding: undefined,
  justifyContent: undefined
  // height: 22,
  // borderWidth: 0,
  // paddingTop: 0,
  // paddingBottom: 0,
  // paddingVertical: 0
}

const ACTION_SHEET_ITEM: ViewStyle = {
  minHeight: 44,
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 16,
}

/**
 * Describe your component here
 */
export const PhoneInput = observer(function PhoneInput(props: PhoneInputProps) {
  const { textProps, colorScheme = Appearance.getColorScheme(), isInvalid, ...rest } = props
  const [ countries, setCountries ] = useState<Array<PickerData>>([])
  const [ search, setSearch ] = useState<string>('')
  const insets = useSafeAreaInsets()
  const phoneInputRef = useRef<BasePhoneInput>()

  const { isOpen, onOpen, onClose } = useDisclose()

  const countriesSorted = useMemo<Array<PickerData>>(() => (
    _.sortBy(countries, (country) => ({ id: '0', vn: '1', es: '2' }[country.iso2] || `${country.label}`))
  ), [countries])

  const countriesFiltered = useMemo<Array<PickerData>>(() => (
    _.filter(countriesSorted, (country) => (country.label || '').toLowerCase().includes((search || '').toLowerCase()))
  ), [countriesSorted, search])

  const iconBaseStyle = useMemo(() => (
    nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme })
  ), [colorScheme]);
  const inputBaseStyle = useMemo(() => (
    nativeBaseTheme.components.InputBase.baseStyle({ colorMode: colorScheme })
  ), [colorScheme]);

  // @ts-ignore
  const renderFlag: ({ imageSource }: { imageSource: number }) => Element = useCallback(({ imageSource }) => (<View style={FLAG}>
    <Image source={imageSource} style={FLAG_IMAGE} />
    <Icon {...iconBaseStyle} as={<MaterialIcons name="keyboard-arrow-down"/>} size="24px" ml="2px"></Icon>
  </View>), [iconBaseStyle])

  useEffect(() => {
    if (phoneInputRef.current) setCountries(phoneInputRef.current.getPickerData() as unknown as Array<PickerData>)
  }, [])

  const onPressItem: (item: PickerData) => any = useCallback((item) => () => {
    phoneInputRef.current.selectCountry(item.iso2)
    onClose();
  }, [phoneInputRef.current])

  const renderItem: ListRenderItem<PickerData> = useCallback(({ item }) => (
    <View style={ACTION_SHEET_ITEM}>
      <Image source={item.image} style={FLAG_IMAGE} />
      <Text text={`${item.label} (${item.dialCode})`} preset={item.iso2 === phoneInputRef.current.getISOCode() ? "title4" : "body2"} colorScheme={colorScheme} isTruncated marginLeft="13px" />
    </View>
  ), [phoneInputRef.current])

  const setSearchDebounce = useMemo(() => _.debounce(setSearch, 333), [])

  const minHeightMax = Dimensions.get("window").height * (Platform.OS === 'web' ? 0.5 : 0.8);
  const minHeightMin = Dimensions.get("window").height * 0.3;
  const estimatedHeight = (44 * countriesFiltered.length)
    + 24 + 32
    + 40 + 7
    + 60
    + insets.bottom;
  let minHeight = minHeightMax;
  if (estimatedHeight < minHeightMin)
    minHeight = minHeightMin;
  else if (estimatedHeight < minHeightMax)
    minHeight = estimatedHeight;

  // @ts-ignore
  return (
    <>
      <View {...inputBaseStyle} { ...(isInvalid ? inputBaseStyle._invalid : {}) } p={0} pl="8px" style={CONTAINER}>
        <BasePhoneInput
          ref={phoneInputRef}
          initialCountry="id"
          offset={10}
          renderFlag={renderFlag}
          onPressFlag={onOpen}
          textStyle={TEXT}
          textProps={{ colorScheme, ...textProps, borderWidth: 0, borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }}
          textComponent={Input}
          {...rest}
        />
      </View>
      <PickerActionsheet
        colorScheme={colorScheme}
        isOpen={isOpen}
        onClose={onClose}
        data={countriesFiltered}
        titleTx="phoneInput.chooseCountry"
        inputProps={{
          onChangeText: setSearchDebounce
        }}
        onPressItem={onPressItem}
        renderItem={renderItem}
        flatListProps={{
          keyExtractor: (item: PickerData) => `${item.key}`,
          estimatedItemSize: 40
        }}
        actionsheetStyle={{
          minHeight,
          ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 0.5 } : {}),
        }}
      />
    </>
  )
})
